import React from 'react';
import { useTranslation } from 'react-i18next';
import {UserProfile} from "../../../../../model/user/profile/UserProfile";
import { useAuth0 } from '../../../../../auth/useAuth0';
import {useAlert} from "../../../../../context/alert/AlertContext";
import {uploadImage, mediaUrl} from "../../../../../service/media/media-service";
import {useTimeFormat, formatBizDateTime} from "../../../../../util/timeFormat";
import {useUser} from "../../../../../auth/UserContext";

type PersonalDetailsProps = {
    profileData: any;
    setProfileData: (data: any) => void;
    setIsModified: (modified: boolean) => void;
};

const PersonalDetailsComponent: React.FC<PersonalDetailsProps> = ({ profileData, setProfileData, setIsModified }) => {
    const { t, i18n } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const { setAlert } = useAlert();
    const timeFormat = useTimeFormat();
    // lastLoginOn lives on the checkUser-sourced user context (not on the
    // findUserSettings/UserProfile payload), so read it from there.
    const { user } = useUser();

    /** Last login of THIS user (moved here from business Details, D10). Formatted
     *  in the business locale + 12/24h — never the browser's. */
    const lastActiveLabel = (() => {
        const raw = user?.lastLoginOn;
        if (!raw) return '—';
        const parsed = new Date(raw);
        if (isNaN(parsed.getTime())) return String(raw);
        return formatBizDateTime(parsed, timeFormat, i18n.language);
    })();

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        const userId = profileData?.id ? String(profileData.id) : null;
        if (!userId) {
            setAlert('danger', 'Perfil ainda carregando — tente novamente.');
            return;
        }
        try {
            // Upload to R2; store only the object key (rendered via mediaUrl).
            const token = await getAccessTokenSilently();
            const key = await uploadImage(file, `user/${userId}`, token);
            setProfileData((prevData: any) => ({ ...prevData, image: key }));
            setIsModified(true);
        } catch (err) {
            setAlert('danger', err instanceof Error ? err.message : 'Falha ao enviar a imagem');
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        event.preventDefault(); // Prevents the default event propagation
        document.getElementById('profilePicture')?.click();
    };

    type UserProfileField = keyof UserProfile | `${keyof UserProfile & string}.${string}`;
    const handleInputChange = (field: UserProfileField, value: any) => {
        setProfileData((prevData: { [x: string]: any; }) => {
            if (field.includes('.')) {
                const [parentKey, childKey] = field.split('.') as [keyof UserProfile, string];
                return {
                    ...prevData,
                    [parentKey]: {
                        ...(prevData[parentKey] || {}),
                        [childKey]: value,
                    },
                };
            } else {
                return {
                    ...prevData,
                    [field]: value,
                };
            }
        });
        setIsModified(true);
    };

    return (
        <div className="side-content-vertical">
            <div className="profile-section-container separated-container">
                <h3 className="section-header">{t('settings.profile.personalDetails.profileSection')}</h3>
                <form className="profile-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="profilePicture">{t('settings.profile.personalDetails.profilePicture')}</label>
                            <input
                                type="file"
                                id="profilePicture"
                                data-cy="profile-picture-input"
                                accept="image/*"
                                onChange={handleImageChange}
                                style={{display: 'none'}}
                            />
                            <img
                                src={mediaUrl(profileData?.image) || 'default-profile.png'}
                                alt="Profile"
                                className="profile-picture"
                                onClick={handleClick}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="firstName">{t('settings.profile.personalDetails.firstName')}</label>
                            <input
                                type="text"
                                id="firstName"
                                data-cy="profile-firstname-input"
                                value={profileData?.firstName || ''}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="lastName">{t('settings.profile.personalDetails.lastName')}</label>
                            <input
                                type="text"
                                id="lastName"
                                data-cy="profile-lastname-input"
                                value={profileData?.lastName || ''}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">{t('settings.profile.personalDetails.email')}</label>
                            <input
                                type="email"
                                id="email"
                                data-cy="profile-email-input"
                                value={profileData?.email || ''}
                                required
                                disabled={true}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="language">{t('settings.profile.personalDetails.language')}</label>
                            <select
                                id="language"
                                data-cy="profile-language-select"
                                value={profileData?.language || 'English'}
                                onChange={(e) => handleInputChange('language', e.target.value)}
                            >
                                <option value="Portuguese">{t('settings.profile.personalDetails.languagePortuguese')}</option>
                                <option value="English">{t('settings.profile.personalDetails.languageEnglish')}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">{t('settings.profile.personalDetails.phone')}</label>
                            <input
                                type="tel"
                                id="phone"
                                data-cy="profile-phone-input"
                                value={profileData?.phoneNumber || ''}
                                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                required
                                pattern="\d*"
                                title="Please enter a valid phone number"
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="lastLoginOn">{t('settings.profile.personalDetails.lastActive')}</label>
                            <input
                                type="text"
                                id="lastLoginOn"
                                data-cy="profile-last-login"
                                value={lastActiveLabel}
                                disabled
                                readOnly
                            />
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PersonalDetailsComponent;
